import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface SupportTicket {
  id: string;
  name: string;
  contactEmail?: string | null;
  topic: string;
  message: string;
  status: string;
  priority: string;
  adminNotes?: string | null;
  createdAt?: string;
}

interface SalesInquiry {
  id: string;
  contactName: string;
  email: string;
  phone?: string | null;
  orgName: string;
  plan: string;
  studentCount?: number | null;
  sports?: string[];
  notes?: string | null;
  status: string;
  createdAt?: string;
}

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  open: 'warning',
  in_progress: 'info',
  waiting: 'default',
  resolved: 'success',
  closed: 'default',
};

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
}

const AdminSupportTickets: React.FC = () => {
  const [rows, setRows] = useState<SupportTicket[]>([]);
  const [salesRows, setSalesRows] = useState<SalesInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const [ticketRes, salesRes] = await Promise.all([
        fetch(`/api/admin/support-tickets${qs}`, {
          headers: token() ? { Authorization: `Bearer ${token()}` } : {},
        }),
        fetch('/api/admin/sales-inquiries', {
          headers: token() ? { Authorization: `Bearer ${token()}` } : {},
        }),
      ]);
      const data = await ticketRes.json();
      const salesData = await salesRes.json();
      if (!data.success) throw new Error(data.message || 'Failed to load');
      setRows(data.data || []);
      setSalesRows(salesData.success ? salesData.data || [] : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const updateSalesStatus = async (id: string, status: string) => {
    try {
      const res = await fetch(`/api/admin/sales-inquiries/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed');
      setSalesRows((prev) => prev.map((r) => (r.id === id ? data.data : r)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const res = await fetch(`/api/admin/support-tickets/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed');
      setRows((prev) => prev.map((r) => (r.id === id ? data.data : r)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Support tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Support inbox from the public Support page, plus sales enquiries from Subscriptions.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(String(e.target.value))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In progress</MenuItem>
              <MenuItem value="waiting">Waiting</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={() => void load()} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No tickets yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.name}</TableCell>
                    <TableCell>{row.topic}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {row.message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status}
                        color={statusColors[row.status] || 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={row.status}
                        onChange={(e) => void updateStatus(row.id, e.target.value)}
                        SelectProps={{ native: true }}
                        sx={{ minWidth: 120 }}
                      >
                        <option value="open">open</option>
                        <option value="in_progress">in_progress</option>
                        <option value="waiting">waiting</option>
                        <option value="resolved">resolved</option>
                        <option value="closed">closed</option>
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="h6" sx={{ fontWeight: 800, mt: 4, mb: 1 }}>
        Sales enquiries
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Pre-sales requests from academies, schools, and corporate teams.
      </Typography>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 800 }}>When</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Students</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Sports</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary">No sales enquiries yet.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              salesRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{row.contactName}</Typography>
                    <Typography variant="body2">{row.email}</Typography>
                    {row.phone ? <Typography variant="body2">{row.phone}</Typography> : null}
                  </TableCell>
                  <TableCell>{row.orgName}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{row.plan}</TableCell>
                  <TableCell>{row.studentCount ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{(row.sports || []).join(', ') || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={statusColors[row.status] || 'default'}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={row.status}
                      onChange={(e) => void updateSalesStatus(row.id, e.target.value)}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 120 }}
                    >
                      <option value="open">open</option>
                      <option value="in_progress">in_progress</option>
                      <option value="waiting">waiting</option>
                      <option value="resolved">resolved</option>
                      <option value="closed">closed</option>
                    </TextField>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminSupportTickets;
